/**
 * A class encapsulating render pipeline passes for drawing. Meant for on-screen rendering, and intended to be lighter weight than SSFXRenderTarget.
 * @module
 */
import { assertIsNotUndefined } from "common/utils/assertOfType";
import * as THREE from "three";
import { defaultOrthoCameraSettings } from "../../CameraSettings";
import { cameraToTHREE } from "../../convert";
import type { HighlightDataTexture } from "../../HighlightDataTexture";
import { pickingMaterial } from "../../materials/pickingMaterial";
import type { WebGLExtendedCapabilities } from "../../WebGLExtendedCapabilities";
import type { EffectComposer } from "../examples/jsm/postprocessing/EffectComposer";
import { effectComposerRender, effectComposerSetSize } from "../examples/jsm/postprocessing/EffectComposer";
import type { EffectComposerPass } from "../examples/jsm/postprocessing/EffectComposerPass";
import { RenderPass } from "../examples/jsm/postprocessing/RenderPass";
import { AOAlgorithm } from "./AOAlgorithm";
import { DepthNormalPass } from "./DepthNormalPass";
import { GTAOPass } from "./GTAOPass";
import {
  MaterialSharedParameterGroup,
  SOFT_SHADOW_CAMERA_FAR_DEFAULT,
  type SharedUniformValues,
} from "./MaterialSharedParameterGroup";
import { RenderPipelineParamsContainer } from "./RenderPipelineParamsContainer";
import { SAOPass2 } from "./SAOPass2";
import { LightProbeVisualizationMode } from "./SHLightProbeConstants";
import { uvCalibrationMaterial } from "./SSFXRendererGlobalResources";
import { SSFXRendererOutput } from "./SSFXRendererOutput";
import type { RendererParams } from "./SSFXRendererParams";
import { applyMaterialSharedParameterGroupDefines, defaultLightProbeRenderParams } from "./SSFXRendererParams";
import type SSFXRendererScene from "./SSFXRendererScene";
import type { LightProbeRenderParams } from "./SSFXRendererTypes";

// Ensure that the depth/normal render target size is evenly divisible by two - that simplifies half-resolution AO rendering.
const DEFAULT_AO_SIZE_GRANULARITY = 2.0;

export class SSFXLightweightRenderTarget {
  private readonly whiteTexture: THREE.DataTexture;

  readonly renderPipelineParams: RenderPipelineParamsContainer;
  readonly lightProbeRenderParams: LightProbeRenderParams;

  private renderPass: RenderPass;
  private overlayPasses: EffectComposerPass[] = [];

  depthNormalPass: DepthNormalPass;
  saoPass: SAOPass2;
  gtaoPass: GTAOPass;

  private renderOneSampleComposer: EffectComposer;

  private camera: THREE.Camera = cameraToTHREE(defaultOrthoCameraSettings, 1);
  private scene: SSFXRendererScene | undefined;

  sampleRenderTargetWidth = 0;
  sampleRenderTargetHeight = 0;

  get setupSupported(): boolean {
    return this.setupSupportedInternal;
  }
  private setupSupportedInternal = false;

  constructor(
    private renderer: THREE.WebGLRenderer,
    private finalRenderWidth: number,
    private finalRenderHeight: number,
    capabilities: WebGLExtendedCapabilities,
  ) {
    this.whiteTexture = new THREE.DataTexture(
      new Uint8Array([255, 255, 255, 255]),
      1,
      1,
      THREE.RGBAFormat,
      THREE.UnsignedByteType,
    );
    this.whiteTexture.needsUpdate = true;

    this.renderPipelineParams = new RenderPipelineParamsContainer(this, capabilities);

    this.lightProbeRenderParams = {
      ...defaultLightProbeRenderParams,
    };

    this.depthNormalPass = new DepthNormalPass(
      this.scene?.scene,
      this.camera,
      capabilities.defaultDepthTextureType,
      undefined,
      DEFAULT_AO_SIZE_GRANULARITY,
      0.4,
    );
    this.depthNormalPass.storePrevViewZRenderTarget = false;
    this.depthNormalPass.params.enableViewZConversion = false;

    this.depthNormalPass.onAfterPass = () => {
      MaterialSharedParameterGroup.instance.setAoMapDimensions({
        aoMapPixelOffset: this.depthNormalPass.paddedPixelOffset,
        aoMapSize: new THREE.Vector2(
          this.depthNormalPass.paddedSize.x / this.depthNormalPass.sizeMultiplier,
          this.depthNormalPass.paddedSize.y / this.depthNormalPass.sizeMultiplier,
        ),
      });
    };

    this.saoPass = new SAOPass2(this.camera, this.depthNormalPass, undefined);

    this.gtaoPass = new GTAOPass(this.camera, this.depthNormalPass, undefined);

    // Composer for rendering single frames / SSAA samples.
    this.renderOneSampleComposer = [];

    this.renderOneSampleComposer.push(this.depthNormalPass);
    this.renderOneSampleComposer.push(this.saoPass);
    this.renderOneSampleComposer.push(this.gtaoPass);

    this.renderPass = new RenderPass(this.scene?.scene, this.camera, undefined, new THREE.Color(1.0, 1.0, 1.0), 1.0);
    this.renderOneSampleComposer.push(this.renderPass);

    this.renderOneSampleComposer.push(this.overlayPasses);
  }

  private updateIntermediateRenderTargetSizes(): boolean {
    const sampleRenderTargetWidth = Math.floor(this.finalRenderWidth);
    const sampleRenderTargetHeight = Math.floor(this.finalRenderHeight);
    if (
      this.sampleRenderTargetWidth !== sampleRenderTargetWidth ||
      this.sampleRenderTargetHeight !== sampleRenderTargetHeight
    ) {
      this.sampleRenderTargetWidth = sampleRenderTargetWidth;
      this.sampleRenderTargetHeight = sampleRenderTargetHeight;
      effectComposerSetSize(this.renderOneSampleComposer, this.sampleRenderTargetWidth, this.sampleRenderTargetHeight);
      return true;
    }
    return false;
  }

  private updateParamsInRenderTarget(container: any, params: any) {
    for (const key of Object.keys(params)) {
      container[key] = params[key];
    }
  }

  applyRenderParams(renderParams: RendererParams) {
    this.updateParamsInRenderTarget(this.renderPipelineParams, renderParams.renderPipelineParams);
    this.updateParamsInRenderTarget(this.depthNormalPass.params, renderParams.depthNormalParams);
    this.updateParamsInRenderTarget(this.saoPass.params, renderParams.saoParams);
    this.updateParamsInRenderTarget(this.gtaoPass.params, renderParams.gtaoParams);
    this.updateParamsInRenderTarget(this.lightProbeRenderParams, renderParams.lightProbeRenderParams);

    applyMaterialSharedParameterGroupDefines(renderParams.softShadowParams);
  }

  private reportedIncompatibleRenderParams: string | undefined;

  private reportIncompatibleRenderParams(errorMsg: string) {
    if (this.reportedIncompatibleRenderParams === errorMsg) {
      return;
    }
    this.reportedIncompatibleRenderParams = errorMsg;
    console.error(errorMsg);
  }

  private setupRenderPipelineForFrame(): boolean {
    if (
      this.renderPipelineParams.output === SSFXRendererOutput.LINEWORK ||
      this.renderPipelineParams.output === SSFXRendererOutput.LINEWORK_WITH_SELECTION ||
      this.renderPipelineParams.output === SSFXRendererOutput.LINEWORK_TRANSPARENT ||
      this.renderPipelineParams.output === SSFXRendererOutput.SCREEN_SPACE_SHADOWS ||
      this.renderPipelineParams.output === SSFXRendererOutput.TRANSMISSION_VISUALIZATION
    ) {
      this.reportIncompatibleRenderParams(
        "Output " +
          SSFXRendererOutput[this.renderPipelineParams.output] +
          " is not supported in lightweight render target",
      );
      return false;
    }
    if (this.renderPipelineParams.screenSpaceShadowsEnabled) {
      this.reportIncompatibleRenderParams("Screen space shadows are not supported in lightweight render target");
      return false;
    }
    if (this.renderPipelineParams.smallScaleAOEnabled) {
      this.reportIncompatibleRenderParams("Small scale AO is not supported in lightweight render target");
      return false;
    }
    if (this.renderPipelineParams.reflectionsEnabled) {
      this.reportIncompatibleRenderParams("Reflections are not supported in lightweight render target");
      return false;
    }

    const toneMapping: THREE.ToneMapping = parseInt(this.renderPipelineParams.toneMapping as any) as THREE.ToneMapping;
    MaterialSharedParameterGroup.instance.sharedDefines.toneMapping = toneMapping;

    const aoPass = this.renderPipelineParams.aoAlgorithm === AOAlgorithm.SAO ? this.saoPass : this.gtaoPass;

    const outputFinal = this.renderPipelineParams.output === SSFXRendererOutput.FINAL_RENDER_WITH_EFFECTS;

    const outputLight =
      this.renderPipelineParams.output === SSFXRendererOutput.LIGHT_PROBE_IRRADIANCE ||
      this.renderPipelineParams.output === SSFXRendererOutput.LIGHT ||
      this.renderPipelineParams.output === SSFXRendererOutput.LIGHT_WITHOUT_DETAIL;

    MaterialSharedParameterGroup.instance.sharedDefines.ssrEnabled = false;
    MaterialSharedParameterGroup.instance.sharedDefines.screenSpaceShadowsEnabled = false;
    MaterialSharedParameterGroup.instance.sharedDefines.smallScaleAOEnabled = false;
    MaterialSharedParameterGroup.instance.sharedDefines.isolateModeSupported = false;
    assertIsNotUndefined(this.scene);

    const needsDepthNormalPassForEffects = this.renderPipelineParams.aoAlgorithm !== AOAlgorithm.NONE;

    this.depthNormalPass.enabled =
      (needsDepthNormalPassForEffects &&
        this.renderPipelineParams.output !== SSFXRendererOutput.PICKING_IDS &&
        this.renderPipelineParams.output !== SSFXRendererOutput.UV_CALIBRATION) ||
      this.renderPipelineParams["output"] === SSFXRendererOutput.NORMAL;

    this.depthNormalPass.params.renderNormalTexture =
      (this.renderPipelineParams.aoAlgorithm !== AOAlgorithm.NONE && aoPass.needsNormalTexture()) ||
      this.renderPipelineParams["output"] === SSFXRendererOutput.NORMAL;

    this.depthNormalPass.params.outputEnabled =
      this.renderPipelineParams["output"] === SSFXRendererOutput.NORMAL ||
      this.renderPipelineParams["output"] === SSFXRendererOutput.DEPTH ||
      (this.renderPipelineParams["output"] >= SSFXRendererOutput.DEPTH_MIP_1 &&
        this.renderPipelineParams["output"] <= SSFXRendererOutput.DEPTH_MIP_10);
    if (this.depthNormalPass.enabled && this.depthNormalPass.params.outputEnabled) {
      this.depthNormalPass.params.output = this.renderPipelineParams.output;
    }
    const outputPhysicalMaterial =
      outputFinal || outputLight || this.renderPipelineParams["output"] === SSFXRendererOutput.BASE_COLOR;

    this.saoPass.enabled = false;
    this.gtaoPass.enabled = false;
    this.gtaoPass.worldUpVector.copy(this.scene.worldUpVector);
    aoPass.enabled =
      this.renderPipelineParams.aoAlgorithm !== AOAlgorithm.NONE &&
      !this.depthNormalPass.params.outputEnabled &&
      this.renderPipelineParams.output !== SSFXRendererOutput.PICKING_IDS &&
      this.renderPipelineParams.output !== SSFXRendererOutput.UV_CALIBRATION &&
      this.renderPipelineParams.output !== SSFXRendererOutput.MATERIAL_ID;
    if (aoPass.enabled) {
      if (
        this.renderPipelineParams.output === SSFXRendererOutput.NORMAL_FOR_AO ||
        this.renderPipelineParams.output === SSFXRendererOutput.AO
      ) {
        aoPass.params.output = this.renderPipelineParams.output;
      } else {
        aoPass.params.output = SSFXRendererOutput.FINAL_RENDER_WITH_EFFECTS;
      }
      this.depthNormalPass.setSizeMultiplier(aoPass.params.aoResolutionMultiplier);
    }

    this.renderPass.enabled =
      outputPhysicalMaterial ||
      this.renderPipelineParams.output === SSFXRendererOutput.PICKING_IDS ||
      this.renderPipelineParams.output === SSFXRendererOutput.UV_CALIBRATION;

    this.renderPass.overrideMaterial = undefined;
    if (this.renderPipelineParams.output === SSFXRendererOutput.PICKING_IDS) {
      this.renderPass.overrideMaterial = pickingMaterial;
    } else if (this.renderPipelineParams.output === SSFXRendererOutput.UV_CALIBRATION) {
      this.renderPass.overrideMaterial = uvCalibrationMaterial();
    }

    if (outputPhysicalMaterial) {
      const useHalfFloatLinearRenderBuffer = this.renderPipelineParams.useHalfFloatLinearRenderBuffer;
      this.scene.applyToneMappingToSkyMaterial(useHalfFloatLinearRenderBuffer, toneMapping);
      const sceneSharedUniformValues = this.scene.getSceneSharedUniformValues(useHalfFloatLinearRenderBuffer);

      const sharedUniformValues: SharedUniformValues = {
        ...sceneSharedUniformValues,

        outputType: this.renderPipelineParams.output,
        ssfxEnabled: true,

        screenSpaceAoMap:
          this.renderPipelineParams.aoAlgorithm === AOAlgorithm.NONE ? this.whiteTexture : aoPass.screenSpaceAoMap,
        screenSpaceReflectionMap: null,
        screenSpaceShadowMap: null,
        renderTargetResolution: [this.sampleRenderTargetWidth, this.sampleRenderTargetHeight],

        probeGridNormalOffsetMultiplier: this.lightProbeRenderParams.probeGridNormalOffsetMultiplier,
        probeGridSmoothBackfaceWeightConstantTerm:
          this.lightProbeRenderParams.probeGridSmoothBackfaceWeightConstantTerm,
        lightProbeIrradianceMultiplier: this.lightProbeRenderParams.lightProbeIrradianceMultiplier,
        substitutedReflectionMultiplier: this.renderPipelineParams.substitutedReflectionMultiplier,

        softShadowCameraFar: SOFT_SHADOW_CAMERA_FAR_DEFAULT,
      };
      MaterialSharedParameterGroup.instance.setSharedUniformValues(sharedUniformValues);
    }

    this.scene.isRenderingWireframe = MaterialSharedParameterGroup.instance.sharedDefines.wireframe;

    if (this.renderPipelineParams.includeOverlayInOutput && this.scene.container) {
      this.scene.container.setupOverlayPasses(this.overlayPasses, this.camera);
    } else {
      this.overlayPasses.length = 0;
    }

    const lightProbeGrids = this.scene.lightProbeGrids;
    for (let i = 0, len = lightProbeGrids.length; i < len; ++i) {
      lightProbeGrids[i].visualizationMode = parseInt(this.lightProbeRenderParams.lightProbeVisualization as any);
      const gridVisible = this.lightProbeRenderParams.lightProbeVisualization !== LightProbeVisualizationMode.None;
      lightProbeGrids[i].visible = gridVisible;
      if (gridVisible) {
        lightProbeGrids[i].updateMatrixWorld();
      }
    }

    return true;
  }

  setScene(scene: SSFXRendererScene) {
    this.scene = scene;
    this.depthNormalPass.scene = scene.scene;
    this.renderPass.scene = scene.scene;
  }

  setCamera(camera: THREE.Camera) {
    this.camera = camera;
    this.depthNormalPass.camera = camera;
    this.saoPass.camera = camera;
    this.gtaoPass.camera = camera;
    this.renderPass.camera = camera;
  }

  setFinalRenderSize(finalRenderWidth: number, finalRenderHeight: number) {
    this.finalRenderWidth = finalRenderWidth;
    this.finalRenderHeight = finalRenderHeight;
  }

  // Returns final render resolution in actual pixels. Pixel ratio is handled outside of SSFXRenderer.
  getFinalRenderSize(): { width: number; height: number } {
    return { width: this.finalRenderWidth, height: this.finalRenderHeight };
  }

  beginFrame() {
    const didUpdateRenderResolution = this.updateIntermediateRenderTargetSizes();
    MaterialSharedParameterGroup.instance.randomSeed = 0.5;
    this.saoPass.randomSeed = 0.5;
    this.gtaoPass.randomSeed = 0.5;
    return { didUpdateRenderResolution };
  }

  setHighlightDataTexture(highlightDataTexture?: HighlightDataTexture) {
    this.depthNormalPass.setHighlightDataTexture(highlightDataTexture);
  }

  renderOneSample(renderTarget: THREE.WebGLRenderTarget | null) {
    this.setupSupportedInternal = this.setupRenderPipelineForFrame();
    if (!this.setupSupportedInternal) {
      return;
    }
    effectComposerRender(this.renderOneSampleComposer, this.renderer, renderTarget);
  }

  dispose() {
    this.depthNormalPass.dispose();
    this.saoPass.dispose();
    this.gtaoPass.dispose();
  }
}
